import type { TitleSegment } from '@/lib/theme'

/**
 * Formes renvoyées par la surface publique de l'API.
 *
 * À partir de la Phase 22, `npm run api:types` génère ces types depuis le
 * schéma OpenAPI ; ils sont ici déclarés à la main pour rester lisibles et
 * pour que toute divergence casse la compilation plutôt que la soirée.
 */

export interface PublicEvent {
  id: string
  slug: string
  edition_year: number
  name: string
  subtitle: string
  slogan: string
  tagline: string
  description: string

  timezone: string
  doors_open_at: string | null
  starts_at: string
  ends_at: string | null

  venue_name: string | null
  address_line1: string | null
  postal_code: string | null
  city: string | null
  country: string | null

  currency: string
  locale: string
  locales: string[]
  theme: Record<string, string>
  /** Le titre composé. Vide = le front retombe sur son découpage. */
  title_style: TitleSegment[]
  social: Record<string, string>
  contact_email: string | null
  contact_phone: string | null

  hero_image_url: string | null
  logo_url: string | null
  /** Uniquement les réglages marqués publics côté serveur. */
  settings: Record<string, unknown>
}

export interface PublicContentBlock {
  key: string
  title: string
  subtitle: string
  body: string
  cta_label: string
  cta_url: string | null
  variant: string | null
  image_url: string | null
  position: number
}


/** Un visage de l'équipe LeYAMO. Ni compte, ni contact : juste qui c'est. */
export interface PublicTeamMember {
  name: string
  role: string
  bio: string
  photo_url: string | null
  position: number
}

/** Une question fréquente, déjà résolue dans la langue du visiteur. */
export interface PublicFaq {
  id: string
  question: string
  answer: string
  category: string | null
  position: number
}

export interface PublicSponsor {
  name: string
  description: string
  url: string | null
  tier: string | null
  logo_url: string | null
  position: number
}

/** Texte multilingue tel que stocké dans un réglage administrable. */
export type I18nValue = Record<string, string>

export function readI18nSetting(
  settings: Record<string, unknown>,
  code: string,
  locale: string,
  fallbackLocale: string,
): string {
  const value = settings[code]
  if (typeof value === 'string') return value
  if (!value || typeof value !== 'object') return ''
  const translations = value as I18nValue
  return translations[locale] ?? translations[fallbackLocale] ?? Object.values(translations)[0] ?? ''
}

export function readBooleanSetting(
  settings: Record<string, unknown>,
  code: string,
  fallback = false,
): boolean {
  const value = settings[code]
  return typeof value === 'boolean' ? value : fallback
}

export function readStringSetting(settings: Record<string, unknown>, code: string): string {
  const value = settings[code]
  return typeof value === 'string' ? value : ''
}
